/**
 * When a photo was taken, read from its EXIF on the phone before the photo is cleaned.
 *
 * This is the one thing the sent-card upload (#56) reads from a photo's hidden data. It
 * orders the photos for pairing and fills in the sent date. The location, and everything
 * else, is never read here, and sanitizeImage drops it all before the upload.
 *
 * Returns the time in milliseconds, read as local time (EXIF has no time zone), or null
 * when the photo is not a JPEG or carries no time. The caller falls back to the file's
 * own modified time.
 */
export function readTakenAt(buffer: ArrayBuffer): number | null {
  try {
    const exif = findExif(new DataView(buffer));
    return exif ? takenAtFromTiff(exif) : null;
  } catch {
    // A truncated or odd file reads past its end; that is "no time", not a failed upload.
    return null;
  }
}

/** The TIFF block inside a JPEG's APP1 "Exif" segment, or null. */
function findExif(view: DataView): DataView | null {
  if (view.byteLength < 4 || view.getUint16(0) !== 0xffd8) return null;
  let offset = 2;
  while (offset + 4 <= view.byteLength) {
    if (view.getUint8(offset) !== 0xff) return null;
    const marker = view.getUint8(offset + 1);
    // Start of scan: the image data follows and there is no metadata after it.
    if (marker === 0xda) return null;
    const length = view.getUint16(offset + 2);
    if (marker === 0xe1 && length >= 8 && readAscii(view, offset + 4, 6) === "Exif\0\0") {
      return new DataView(view.buffer, view.byteOffset + offset + 10, length - 8);
    }
    offset += 2 + length;
  }
  return null;
}

const DATE_TIME = 0x0132;
const EXIF_IFD = 0x8769;
const DATE_TIME_ORIGINAL = 0x9003;
const DATE_TIME_DIGITIZED = 0x9004;

function takenAtFromTiff(tiff: DataView): number | null {
  const order = readAscii(tiff, 0, 2);
  if (order !== "II" && order !== "MM") return null;
  const little = order === "II";
  const first = tiff.getUint32(4, little);
  const main = readIfd(tiff, first, little);
  const exifOffset = main.get(EXIF_IFD);
  const exif = exifOffset === undefined ? new Map<number, number>() : readIfd(tiff, tiff.getUint32(exifOffset + 8, little), little);
  // When the shutter went, then when it was saved, then when the file was last changed.
  for (const [ifd, tag] of [[exif, DATE_TIME_ORIGINAL], [exif, DATE_TIME_DIGITIZED], [main, DATE_TIME]] as const) {
    const entry = ifd.get(tag);
    if (entry === undefined) continue;
    const time = parseExifTime(readAsciiValue(tiff, entry, little));
    if (time !== null) return time;
  }
  return null;
}

/** Tag number to the offset of its 12-byte entry. */
function readIfd(tiff: DataView, offset: number, little: boolean) {
  const entries = new Map<number, number>();
  const count = tiff.getUint16(offset, little);
  for (let index = 0; index < count; index += 1) {
    const entry = offset + 2 + index * 12;
    entries.set(tiff.getUint16(entry, little), entry);
  }
  return entries;
}

/** An ASCII entry's text: inline when it fits in four bytes, elsewhere in the block when not. */
function readAsciiValue(tiff: DataView, entry: number, little: boolean) {
  const count = tiff.getUint32(entry + 4, little);
  const start = count <= 4 ? entry + 8 : tiff.getUint32(entry + 8, little);
  return readAscii(tiff, start, count).replace(/\0+$/, "");
}

function readAscii(view: DataView, start: number, length: number) {
  let text = "";
  for (let index = 0; index < length; index += 1) text += String.fromCharCode(view.getUint8(start + index));
  return text;
}

/** "2026:09:24 14:02:31", which cameras write when the clock was never set as "0000:00:00 00:00:00". */
function parseExifTime(text: string): number | null {
  const match = text.match(/^(\d{4}):(\d{2}):(\d{2}) (\d{2}):(\d{2}):(\d{2})/);
  if (!match) return null;
  const [year, month, day, hour, minute, second] = match.slice(1).map(Number);
  if (year < 1900 || month < 1 || month > 12 || day < 1 || day > 31) return null;
  return new Date(year, month - 1, day, hour, minute, second).getTime();
}
